import {
  Button,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { Input } from "./Input";
import Loading from "./Loading";
import { CategoryList, categoryUpdate } from "../services/payable";

const CategoryEdit = ({
  open,
  onClose,
  refetch,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: categoryUpdate,
    onSuccess: () => {
      toast.success("Categoria atualizada com sucesso!");
      refetch?.();
      onClose();
    },
    onError: (e: any) => {
      const message =
        e?.response?.data?.message ??
        e?.message ??
        "Erro ao atualizar a categoria!";
      toast.error(message);
    },
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["category"],
    queryFn: async () => await CategoryList(),
  });

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: { id: 0, name: "" },
    onSubmit: ({ id, name }) => {
      const payload = { id: Number(id), name: name.trim() };
      if (!payload.id) {
        toast.warn("Selecione uma categoria.");
        return;
      }
      if (!payload.name) {
        toast.warn("Informe o nome da categoria.");
        return;
      }
      mutate(payload);
    },
  });

  return (
    <Dialog maxWidth="xs" open={open} onClose={onClose}>
      <Loading loading={isPending} />
      <DialogContent
        sx={{ backgroundColor: "secondary.main" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10 }}>
            <Typography fontSize={24} fontWeight={600}>
              Editar Categorias
            </Typography>
          </Grid2>

          <Grid2 size={{ xs: 2 }}>
            <IconButton onClick={onClose} aria-label="Fechar">
              <IconX />
            </IconButton>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Stack>
              <Typography fontSize={14} fontWeight={600}>
                Selecione uma categoria
              </Typography>
              <Select
                id="id"
                name="id"
                value={values.id || ""}
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  setFieldValue("id", selectedId);

                  const selected = data?.find((c: any) => c.id === selectedId);
                  if (selected) {
                    setFieldValue("name", selected.name);
                  }
                }}
                fullWidth
                displayEmpty
                sx={{ marginTop: 1 }}
              >
                <MenuItem value="" disabled>
                  {isLoading || isFetching
                    ? "Carregando categorias..."
                    : "Selecione uma categoria"}
                </MenuItem>

                {Array.isArray(data) &&
                  data
                    .filter((c: any) => c?.status === true)
                    .map((c: any) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}

                {!isLoading &&
                  !isFetching &&
                  Array.isArray(data) &&
                  data.filter((c: any) => c?.status === true).length === 0 && (
                    <MenuItem value="" disabled>
                      Nenhuma categoria ativa
                    </MenuItem>
                  )}
              </Select>
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Stack>
              <Input
                id="name"
                name="name"
                label="Nome da categoria"
                placeholder="Ex.: Despesas Fixas"
                value={values.name}
                onChange={handleChange}
                autoFocus
              />
            </Stack>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!values.name.trim() || !values.id || isPending}
              onClick={() => handleSubmit()}
            >
              Salvar
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryEdit;
